import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';

import { ApiService } from 'src/app/services/api/api.service';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Subscription } from 'rxjs';

import utils from '../../helpers/utils';

@Component({
  selector: 'app-input',
  templateUrl: './input.component.html',
  styleUrls: ['./input.component.scss'],
})
export class InputComponent implements OnInit {
  @Input() id?: string = utils.makeID();
  @Input() name?: string;
  @Input() placeholder: string = 'Digite';
  @Input() label?: string;
  @Input() noResize?: boolean = true;
  @Input() type = 'text';
  @Input() alwaysValid: boolean = false;

  @Input() standalone: boolean = false;
  @Input() value: string = '';

  @Input() pattern?: string;

  @Input() min?: number | string;
  @Input() max?: number | string;

  @Input() maxLength: number = 150;

  @Input() showPassword: boolean = true;

  @Input() hasDate?: boolean;

  // Form control
  @Input() controlName: string = '';
  @Input() group?: any = new FormGroup({});

  @Input() helpText?: string;

  @Input() errorText: string = 'Campo inválido';

  @Input() readOnly: boolean = false;
  // @Input() validated: boolean = false;
  @Input() rows: number = 0;

  @Input() isUpperCase = false;

  @Input() button: string = '';

  @Input() isDocument: boolean = false;

  @Input() hasError: boolean = false;

  @Output() onButtonClick: EventEmitter<MouseEvent> = new EventEmitter();

  @Output() onChange: EventEmitter<string> = new EventEmitter();

  error: boolean = false;

  isPassword = false;

  validated = false;

  private valueChangeSubscription?: Subscription;
  private documentSubscription?: Subscription;

  constructor(private apiService: ApiService) {}

  ngOnDestroy(): void {
    if (this.valueChangeSubscription) {
      this.valueChangeSubscription.unsubscribe();
    }
    if (this.documentSubscription) {
      this.documentSubscription.unsubscribe();
    }
  }

  getAutocomplete(): string {
    if (this.type === 'password') {
      // Verificar se é senha atual ou nova senha baseado no controlName
      if (
        this.controlName &&
        (this.controlName.includes('current') ||
          this.controlName.includes('old'))
      ) {
        return 'current-password';
      }
      return 'new-password';
    }
    if (this.type === 'email') {
      return 'email';
    }
    return 'off';
  }

  onButtonClicked(event: MouseEvent): void {
    this.onButtonClick.emit(event);
  }

  handleChange(event: any): void {
    this.onChange.emit(event.target.value);
  }

  ngOnInit(): void {
    if (this.showPassword) {
      this.isPassword = this.type === 'password';
    }

    if (
      this.type === 'date' &&
      (this.controlName === 'birth_date' || this.name === 'birth_date')
    ) {
      const today = new Date();

      const minAge = 18;
      const maxAge = 120;

      const minDate = new Date(
        today.getFullYear() - maxAge,
        today.getMonth(),
        today.getDate()
      )
        .toISOString()
        .split('T')[0];

      const maxDate = new Date(
        today.getFullYear() - minAge,
        today.getMonth(),
        today.getDate()
      )
        .toISOString()
        .split('T')[0];

      this.max = maxDate;
      this.min = minDate;
    }

    if (this.isDocument) {
      this.documentSubscription = this.group.controls[
        this.controlName
      ].valueChanges
        .pipe(debounceTime(400), distinctUntilChanged())
        .subscribe((values: any) => {
          this.documentInUse(utils.onlyNumbers(values));
        });
    }

    // Remove espaços no início do campo automaticamente
    if (
      !this.standalone &&
      this.group &&
      this.group.controls[this.controlName]
    ) {
      this.valueChangeSubscription = this.group.controls[
        this.controlName
      ].valueChanges
        .pipe(distinctUntilChanged())
        .subscribe((value: string) => {
          if (value && typeof value === 'string' && value.startsWith(' ')) {
            const trimmedValue = value.replace(/^\s+/, '');
            this.group.controls[this.controlName].setValue(trimmedValue, {
              emitEvent: false,
            });
          }
        });
    }
  }

  filledDate() {
    if (this.type === 'date') {
      if (this.standalone) {
        return this.value !== null && this.value !== '';
      } else {
        const control = this.group.controls[this.controlName];

        return control.value !== null && control.value !== '';
      }
    }
    return false;
  }

  getClasses(): string {
    if (this.error) {
      return 'error';
    }

    return '';
  }

  getInvalid(): boolean {
    if (this.standalone) {
      return this.hasError;
    } else {
      let invalid = false;

      if (this.group) {
        const control = this.group.controls[this.controlName];

        if (control.invalid) {
          invalid = true;
        }

        // Verifica erros do FormGroup (para validações como passwordsMismatch)
        if (
          this.controlName === 'confirmPassword' &&
          this.group.errors &&
          this.group.errors['passwordsMismatch']
        ) {
          invalid = true;
        }
      }

      return invalid;
    }
  }

  getTouched(): boolean {
    if (this.standalone) {
      return true;
    } else {
      let touched = false;

      if (this.readOnly) return touched;

      if (this.group) {
        const control = this.group.controls[this.controlName];

        if (control) {
          if (control.touched) {
            touched = true;
          }
        } else {
          console.error(`Control of ${this.controlName} not found!`);
        }
      }

      return touched;
    }
  }

  getRequired(): boolean {
    if (this.standalone) {
      return true;
    } else {
      const control = this.group.controls[this.controlName];

      if (control && control.validator) {
        let required = false;

        const validator = control.validator({} as AbstractControl);

        if (validator && validator.required) {
          required = true;
        }

        return required;
      }
    }

    return false;
  }

  getError(): boolean {
    if (this.alwaysValid) return false;
    const control = this.group?.controls[this.controlName];
    // Mostra erro se o campo foi alterado (dirty) e está inválido
    return control && (control.dirty || control.touched) && this.getInvalid();
  }

  getErrorMessage(): string {
    if (this.standalone) {
      return this.errorText;
    }

    if (!this.group) {
      return this.errorText;
    }

    // Verifica erros do FormGroup primeiro (para validações como passwordsMismatch)
    // Isso garante que a mensagem de senhas diferentes tenha prioridade sobre "Campo obrigatório"
    if (
      this.controlName === 'confirmPassword' &&
      this.group.errors &&
      this.group.errors['passwordsMismatch']
    ) {
      const passwordControl = this.group.controls['password'];
      const confirmPasswordControl = this.group.controls['confirmPassword'];

      // Se pelo menos uma das senhas foi preenchida e as senhas não coincidem, mostra "senhas diferem"
      if (passwordControl && passwordControl.value) {
        return 'Senhas diferem';
      }
    }

    const control = this.group.controls[this.controlName];
    if (!control || !control.errors) {
      // Se não há erros no control, mas há erro no FormGroup (passwordsMismatch)
      if (
        this.controlName === 'confirmPassword' &&
        this.group.errors &&
        this.group.errors['passwordsMismatch']
      ) {
        return 'Senhas diferem';
      }
      return this.errorText;
    }

    const errors = control.errors;

    // Verifica erros específicos primeiro
    // Validações de nome completo (HEAD)
    if (errors['fullNameIncomplete']) {
      return 'O nome deve estar completo';
    }
    if (errors['fullNameTitle']) {
      return 'Não é permitido usar títulos ou saudações no nome';
    }
    if (errors['fullNameAbbreviated']) {
      return 'Não é permitido usar abreviações no nome';
    }
    if (errors['fullNameNumbers']) {
      return 'O nome não pode conter números';
    }
    if (errors['fullNameDots']) {
      return 'O nome não pode conter pontos';
    }
    if (errors['fullName']) {
      return 'Nome completo inválido';
    }

    // Validações de documento
    if (errors['cpf'] || errors['cnpj']) {
      return 'Documento inválido';
    }

    if (errors['documentInUse']) {
      return 'Este documento já está cadastrado';
    }

    // Validações de email
    if (errors['email']) {
      return 'Email inválido';
    }

    if (errors['invalidEmailFormat']) {
      return 'Digite um email válido';
    }

    // Validações de telefone
    if (errors['telefone'] || errors['celular']) {
      return 'Telefone inválido';
    }

    // Validações de CEP
    if (errors['cepIncomplete']) {
      return 'CEP incompleto';
    }

    if (errors['cep']) {
      return 'CEP inválido';
    }

    // Validações de nome (versão incoming)
    if (errors['minNames']) {
      return 'Informe pelo menos dois nomes';
    }

    if (errors['abbreviation']) {
      return 'Não são permitidas abreviações no nome completo';
    }

    // Validações de espaços
    if (errors['multipleSpaces']) {
      return 'Não é permitido espaços múltiplos';
    }

    if (errors['notOnlyWhitespace']) {
      return 'Este campo não pode conter apenas espaços';
    }

    if (errors['noSpaces']) {
      return 'Este campo não pode conter espaços';
    }

    // Validações de URL
    if (errors['invalidUrlFormat']) {
      return 'URL inválida';
    }

    if (errors['maxDecimalPlaces']) {
      return 'Máximo de 4 casas decimais permitidas';
    }

    // Para confirmPassword, verifica se há erro de mismatch antes de mostrar "Campo obrigatório"
    if (errors['required']) {
      // Se é o campo confirmPassword e há erro de mismatch no FormGroup, mostra mensagem de mismatch
      if (
        this.controlName === 'confirmPassword' &&
        this.group.errors &&
        this.group.errors['passwordsMismatch']
      ) {
        return 'Senhas diferem';
      }
      return 'Campo obrigatório';
    }

    if (errors['minlength']) {
      return `Mínimo de ${errors['minlength'].requiredLength} caracteres`;
    }

    if (errors['maxlength']) {
      return `Máximo de ${errors['maxlength'].requiredLength} caracteres`;
    }

    if (errors['pattern']) {
      if (this.isDocument) {
        return 'Documento inválido';
      }
      return 'Formato inválido';
    }

    return this.errorText;
  }

  passwordReveal(): void {
    if (this.type === 'password') {
      this.type = 'text';
    } else {
      this.type = 'password';
    }
  }

  async documentInUse(document: string) {
    try {
      const res = await this.apiService.post<ValidateFieldResponse>({
        route: 'validate-document-exists/',
        body: { document: document },
      });

      if (res.exists) {
        this.errorText = 'Este documento já está cadastrado';
        this.error = true;
        this.group.controls[this.controlName].setErrors({
          documentInUse: true,
        });
      } else {
        this.error = false;
        // Remove apenas o erro de documentInUse, mantendo outros erros
        const currentErrors = this.group.controls[this.controlName].errors;
        if (currentErrors && currentErrors['documentInUse']) {
          delete currentErrors['documentInUse'];
          if (Object.keys(currentErrors).length === 0) {
            this.group.controls[this.controlName].setErrors(null);
          } else {
            this.group.controls[this.controlName].setErrors(currentErrors);
          }
        }
      }
    } catch (error) {
      this.error = false;
      this.group.controls[this.controlName].setErrors(null);
    }
  }
}
